// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — shopPreview.jsx   ·   THE PICTURE ON A SHOP CARD
// ────────────────────────────────────────────────────────────────────
// ⭐ caP'n, 2026-09-25: "the bunni basket store doesn't have a preview
// picture for the skies etc … the ones it does have pictures for, they're
// way too small it should be like top half is picture bottom half is the
// text."
//
// So every card in Bonnie's Bunni Basket opens on a picture band. Pieces use
// their own 2D drawing (Thumb). Worn things paint from the SAME recipes the
// 3D scenes use — a sky from its dome painting (or its Blender dome, when it
// has one) plus its eclipse/sun; a ground from its ground texture; a Hall
// finish from its tint; a light from its mood — so the picture can never
// drift from what you actually get.
// ════════════════════════════════════════════════════════════════════
import { useMemo } from "react";
import { ITEMS, KIND, SHELF_META, moodOf } from "./mineriumCatalogue";
import { SKY_RECIPES, skyDomeTex } from "./ieSkies";
import { GROUND_RECIPES, groundTex } from "./Minerium3D";
import { LayoutMinimap } from "./hallLayoutThumb";
import { Thumb } from "./buildKit";
import { UI } from "./theme";

export const PREVIEW_H = 150;
const BASE = process.env.PUBLIC_URL || "";

const imgOf = (tex) => { try { return tex?.image?.toDataURL?.("image/png") || null; } catch { return null; } };

function SkyPicture({ id }) {
  const it = ITEMS[id];
  const key = SKY_RECIPES[id] ? id : (SKY_RECIPES[it?.ascendedFrom] ? it.ascendedFrom : "sky_default");
  const spec = SKY_RECIPES[key];
  const src = useMemo(() => (spec.hd?.dome ? `${BASE}/${spec.hd.dome}` : imgOf(skyDomeTex(key, spec))), [key, spec]);
  const r = spec.reading || {};
  const ecl = r.eclipse?.show === false ? null : (spec.eclipse?.r > 0 ? spec.eclipse : null);
  const rim = r.eclipse?.rim || ecl?.tint, crack = r.eclipse?.crack || ecl?.crack;
  const sun = r.sun;
  const holes = (r.holes || []).slice(0, 3);
  return (
    <div style={{ position: "absolute", inset: 0, background: `url("${src}") center 30% / cover` }}>
      {/* the Blender domes already carry their own wound — no drawn eclipse on top */}
      {!spec.hd && ecl && (
        <div style={{
          position: "absolute", left: "50%", top: `${Math.round((1 - (ecl.y ?? 0.6)) * 100)}%`,
          width: 58, height: 58, transform: "translate(-50%,-10%)", borderRadius: "50%",
          background: "#05020a", boxShadow: `0 0 0 2px ${rim}, 0 0 22px 6px ${rim}88`,
        }}>
          {crack && <div style={{ position: "absolute", left: "48%", top: "-8%", width: 2, height: "116%",
                                  background: crack, transform: "rotate(18deg)", boxShadow: `0 0 8px ${crack}` }} />}
        </div>
      )}
      {!spec.hd && holes.map((h, i) => (
        <div key={i} style={{
          position: "absolute", left: `${50 + h.x * 30}%`, top: `${60 - h.y * 30}%`,
          width: 16 + h.size * 16, height: 16 + h.size * 16, transform: "translate(-50%,-50%)", borderRadius: "50%",
          background: "#000", boxShadow: `0 0 0 2px ${h.hot}, 0 0 14px 4px ${h.warm || h.hot}88`,
        }} />
      ))}
      {sun && (
        <div style={{
          position: "absolute", left: "50%", bottom: "12%", width: 64, height: 64, transform: "translateX(-50%)",
          borderRadius: "50%", background: `radial-gradient(circle, ${sun.core}, ${sun.edge} 70%, ${sun.edge}00)`,
          boxShadow: `0 0 30px 10px ${sun.edge}66`,
        }} />
      )}
      {spec.sea && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "16%",
                      background: `linear-gradient(${spec.sea}00, ${spec.sea}55)` }} />
      )}
    </div>
  );
}

function GroundPicture({ id }) {
  const key = GROUND_RECIPES[id] ? id : "ground_grass";
  const src = useMemo(() => imgOf(groundTex(key, GROUND_RECIPES[key])), [key]);
  const glow = GROUND_RECIPES[key].glow;
  return (
    <div style={{ position: "absolute", inset: 0, background: `url("${src}") center / 180px 180px repeat`,
                  boxShadow: glow ? `inset 0 0 40px ${glow}55` : "none" }} />
  );
}

function FinishPicture({ it }) {
  const src = it.finish ? it : ITEMS[it.ascendedFrom] || it;
  const tint = src.finish?.tint || "#555", runner = src.finish?.runner;
  const wall = it.kind === KIND.HALL_WALL;
  return (
    <div style={{ position: "absolute", inset: 0,
                  background: wall
                    ? `repeating-linear-gradient(0deg, ${tint} 0 26px, ${tint}cc 26px 28px), ${tint}`
                    : `repeating-conic-gradient(${tint} 0 25%, ${tint}bb 0 50%) 0 0 / 56px 56px` }}>
      {runner && !wall && <div style={{ position: "absolute", top: 0, bottom: 0, left: "38%", width: "24%",
                                        background: `${runner}55`, borderLeft: `2px solid ${runner}99`, borderRight: `2px solid ${runner}99` }} />}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, #0000 40%, #0006)" }} />
    </div>
  );
}

function LightPicture({ id }) {
  const m = moodOf(id) || {};
  const key = m.key?.[0] || "#ffdca4", fill = m.fill?.[0] || "#B388FF", flame = m.flame || "#ffb84d", fog = m.fog || "#0e0a16";
  return (
    <div style={{ position: "absolute", inset: 0,
                  background: `radial-gradient(ellipse at 50% 85%, ${m.pool || key}cc, ${key}44 32%, #0000 60%),
                               radial-gradient(ellipse at 15% 20%, ${fill}66, #0000 55%), ${fog}` }}>
      {[30, 50, 70].map((x) => (
        <div key={x} style={{ position: "absolute", left: `${x}%`, bottom: "30%", width: 10, height: 18, transform: "translateX(-50%)",
                              borderRadius: "50% 50% 40% 40%", background: flame, boxShadow: `0 0 16px 6px ${flame}99` }} />
      ))}
    </div>
  );
}

// ⭐ the band itself — full card width, the picture fills it
export function ShopPreview({ itemId, height = PREVIEW_H }) {
  const it = ITEMS[itemId];
  if (!it) return null;
  const tint = (SHELF_META[it.shelf] || SHELF_META.fable).tint;
  let body;
  switch (it.kind) {
    case KIND.SKY: body = <SkyPicture id={itemId} />; break;
    case KIND.GROUND: body = <GroundPicture id={itemId} />; break;
    case KIND.HALL_FLOOR: case KIND.HALL_WALL: body = <FinishPicture it={it} />; break;
    case KIND.HALL_LIGHT: body = <LightPicture id={itemId} />; break;
    case KIND.HALL_LAYOUT:
      body = <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><LayoutMinimap itemId={itemId} width={200} /></div>;
      break;
    default:
      body = (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center",
                      background: `radial-gradient(circle at 50% 65%, ${tint}33, ${UI.panelSolid} 72%)` }}>
          <Thumb itemId={itemId} size={height - 16} tint={tint} />
        </div>
      );
  }
  return (
    <div style={{ position: "relative", height, margin: "-11px -11px 2px", overflow: "hidden",
                  borderRadius: `${UI.rSm}px ${UI.rSm}px 0 0`, borderBottom: `1px solid ${tint}44`, background: UI.panelSolid }}>
      {body}
    </div>
  );
}
